/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */
import { Dispatch, SetStateAction, useMemo, useRef } from 'react';
import { parseKahuHref } from '@/utils/kahu-link-helpers';
import { kahuCopyInfoType } from 'types/kahu';

import { usePagination } from '@/hooks/usePagination';
import { FACT_NAME_LABEL_MAP } from '@/components/display-page/fact-name-label-map';

import { KahuListPagerDock } from '../kahu-list-pager-dock';
import { PromptListSkeleton } from '../prompt-list';
import { TraceCard } from './trace-card';
import { isTraceListIncomplete, parseTraceList } from './trace-list-utils';

const TRACES_PER_PAGE = 5;

export function TraceList({
  content,
  messageId,
  copiedInfo,
  setCopiedInfo,
  isStreaming = false,
}: {
  content: string;
  messageId: string;
  copiedInfo: kahuCopyInfoType | null;
  setCopiedInfo: Dispatch<SetStateAction<kahuCopyInfoType | null>>;
  isStreaming?: boolean;
}) {
  const blocks = useMemo(() => parseTraceList(content), [content]);
  const listRef = useRef<HTMLDivElement>(null);

  const {
    pageItems,
    page,
    setPage,
    totalPages,
    total,
    rangeStart,
    rangeEnd,
    showPager,
  } = usePagination(blocks, TRACES_PER_PAGE);

  if (isTraceListIncomplete(content) || blocks.length === 0) {
    return <PromptListSkeleton />;
  }

  // Entity type comes from the response: every item links to
  // okahu://apps/<app>/facts/<factName>/<id>, and a fenced list is one type.
  // Drive the header label and pager noun off that factName via the shared map.
  const parsed = parseKahuHref(blocks[0].href);
  const factName = parsed.type === 'fact' ? parsed.factName : '';
  const fact = FACT_NAME_LABEL_MAP[factName];
  const idLabel = fact?.idLabel || (factName ? `${factName} ID` : 'ID');
  const noun = (fact?.label || factName || 'results').toLowerCase();

  // While streaming, the list is still growing, so pin to the first page and
  // hide the pager until the count is final.
  const pagerVisible = showPager && !isStreaming;

  return (
    <div ref={listRef} className="mt-3 flex w-full flex-col gap-3">
      {pageItems.map((block, i) => {
        const absoluteIndex = rangeStart - 1 + i;
        return (
          <TraceCard
            key={`${block.traceId}-${absoluteIndex}`}
            block={block}
            index={absoluteIndex}
            idLabel={idLabel}
            messageId={messageId}
            copiedInfo={copiedInfo}
            setCopiedInfo={setCopiedInfo}
          />
        );
      })}

      {pagerVisible && (
        <KahuListPagerDock
          listRef={listRef}
          page={page}
          setPage={setPage}
          totalPages={totalPages}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          total={total}
          noun={noun}
        />
      )}
    </div>
  );
}
